// React
import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { useFormikContext } from 'formik'

// Components
import { LocationSelect } from 'ui/components/Picker/LocationPicker/Select'

// Hooks
import { useLocationsData } from 'ui/components/Picker/LocationPicker/useLocationData'
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { isLocationInputEnabled } from 'ui/components/Picker/utils'
import { featureEnabled, FeatureFlags } from 'back_office/v2/utils/utils'

export const LocationPicker = ({ showAddress = true, ...props }) => {
  const picker = usePicker()
  const form = useFormikContext()

  const { options, hasLocations, startLocations, stopLocations, isSingleLocationSelect } = useLocationsData({
    ...props,
    locations: props.locations
  })

  const Wrapper = props.WrapperComponent || Fragment

  const handleLocationChange = (id, type) => {
    if (type === 'both') {
      form.setValues({
        ...form.values,
        start_location_id: id,
        stop_location_id: id
      })

      props.onChange?.({
        start_location_id: id,
        stop_location_id: id
      })
    } else {
      const field = `${type}_location_id`

      form.setFieldValue(field, id)

      props.onChange?.({
        [field]: id
      })
    }
  }

  const showDeliveryDistances =
    props.showDeliveryDistances && props.deliveryAddressPresent && featureEnabled(FeatureFlags.DELIVERIES)

  if (!hasLocations) return null

  if ((props.isSingleLocationSelect || isSingleLocationSelect) && startLocations.size() > 1) {
    return (
      <Wrapper {...(props.WrapperComponent && { disableStyling: picker.type === 'order' })}>
        <LocationSelect
          name="start_location_id"
          options={options}
          locations={props.locations}
          onChange={(id) => handleLocationChange(id, 'both')}
          data-tid="Start and stop location dropdown"
          disabled={props.disable?.start_location_id}
          title={props.locationSelectTitle}
          icon={props.locationSelectIcon}
          deliveryDistances={props.deliveryDistances}
          showDeliveryDistances={showDeliveryDistances}
          label={props.label || props.startLocationLabel}
          showAddress={showAddress}
        />
      </Wrapper>
    )
  }
  return (
    <Wrapper {...(props.WrapperComponent && { disableStyling: picker.type === 'order' })}>
      {isLocationInputEnabled('start', { scope: picker.type }) && (
        <LocationSelect
          name="start_location_id"
          options={options}
          locations={startLocations}
          onChange={(id) => handleLocationChange(id, 'start')}
          data-tid="Start location dropdown"
          disabled={props.disable?.start_location_id}
          title={props.locationSelectTitle || props.startLocationSelectTitle}
          icon={props.locationSelectIcon || props.startLocationSelectIcon}
          label={props.startLocationLabel || props.label}
          showAddress={showAddress}
        />
      )}
      {isLocationInputEnabled('stop', { scope: picker.type }) && (
        <LocationSelect
          name="stop_location_id"
          options={options}
          locations={stopLocations}
          onChange={(id) => handleLocationChange(id, 'stop')}
          data-tid="Stop location dropdown"
          disabled={props.disable?.stop_location_id}
          title={props.locationSelectTitle || props.stopLocationSelectTitle}
          icon={props.locationSelectIcon || props.stopLocationSelectIcon}
          label={props.stopLocationLabel || props.label}
          showAddress={showAddress}
        />
      )}
    </Wrapper>
  )
}

LocationPicker.propTypes = {
  locations: PropTypes.object,
  disable: PropTypes.shape({
    start_location_id: PropTypes.bool,
    stop_location_id: PropTypes.bool
  }),
  locationSelectTitle: PropTypes.node,
  startLocationSelectTitle: PropTypes.node,
  stopLocationSelectTitle: PropTypes.node,
  locationSelectIcon: PropTypes.string,
  startLocationSelectIcon: PropTypes.string,
  stopLocationSelectIcon: PropTypes.string,
  deliveryDistances: PropTypes.array,
  showDeliveryDistances: PropTypes.bool,
  showAddress: PropTypes.bool,
  deliveryAddressPresent: PropTypes.bool,
  isSingleLocationSelect: PropTypes.bool,
  onChange: PropTypes.func,
  WrapperComponent: PropTypes.any,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  startLocationLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  stopLocationLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
}
